import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { LoadingOverlay, useAlert } from '../../components';
import { getActivePaymentChannels, createRechargeRequest } from '../../services/firestoreService';
import { formatCurrency, isValidUTR, copyToClipboard } from '../../utils/helpers';
import { pickImage } from '../../utils/imageUtils';
import QRCode from 'qrcode';
import './RechargeScreen.css';

const MIN_RECHARGE = 620;
const QUICK_AMOUNTS = [620, 2200, 5600, 12900, 17999, 27700];

export const RechargeScreen = () => {
    const navigate = useNavigate();
    const { userData } = useAuth();
    const alert = useAlert();

    const [channels, setChannels] = useState([]);
    const [selectedChannel, setSelectedChannel] = useState(null);
    const [amount, setAmount] = useState('');
    const [utrNumber, setUtrNumber] = useState('');
    const [proofBase64, setProofBase64] = useState(null);
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [step, setStep] = useState(1); // 1: Amount/Channel, 2: Payment Gateway
    const [timeLeft, setTimeLeft] = useState(300); // 5 minutes in seconds
    const [dynamicQRCode, setDynamicQRCode] = useState(null);
    const [transitioning, setTransitioning] = useState(false);

    useEffect(() => {
        loadChannels();
    }, []);

    // Hide WhatsApp float on step 2
    useEffect(() => {
        if (step === 2) {
            document.body.classList.add('hide-whatsapp-float');
        } else {
            document.body.classList.remove('hide-whatsapp-float');
        }
        return () => document.body.classList.remove('hide-whatsapp-float');
    }, [step]);

    useEffect(() => {
        let timer;
        if (step === 2 && timeLeft > 0) {
            timer = setInterval(() => {
                setTimeLeft(prev => prev - 1);
            }, 1000);
        } else if (timeLeft === 0) {
            alert.showInfo('Time Expired', 'Payment time has expired. Please try again.');
            setStep(1);
            setTimeLeft(300);
        }
        return () => clearInterval(timer);
    }, [step, timeLeft]);

    // Generate dynamic QR code when step 2 is reached
    useEffect(() => {
        if (step === 2 && selectedChannel?.upiId && amount) {
            generateDynamicQRCode();
        }
    }, [step, selectedChannel, amount]);

    const formatTime = (seconds) => {
        const mins = Math.floor(seconds / 60);
        const secs = seconds % 60;
        return `${mins}:${secs.toString().padStart(2, '0')}`;
    };

    const loadChannels = async () => {
        try {
            const data = await getActivePaymentChannels();
            setChannels(data);
            if (data.length > 0) {
                setSelectedChannel(data[0]);
            }
        } catch (error) {
            alert.showError('Error', 'Failed to load payment channels');
        } finally {
            setLoading(false);
        }
    };

    const generateDynamicQRCode = async () => {
        if (!selectedChannel?.upiId || !amount) return;

        const upiId = selectedChannel.upiId;
        const amountValue = parseInt(amount);
        const merchantName = 'NEXTRON';
        const transactionNote = 'Recharge';

        const upiString = `upi://pay?pa=${upiId}&pn=${encodeURIComponent(merchantName)}&am=${amountValue}&cu=INR&tn=${encodeURIComponent(transactionNote)}&mode=02`;

        try {
            const qrCodeDataUrl = await QRCode.toDataURL(upiString, {
                width: 300,
                margin: 2,
                color: {
                    dark: '#000000',
                    light: '#FFFFFF'
                }
            });

            setDynamicQRCode(qrCodeDataUrl);
        } catch (error) {
            console.error('Error generating QR code:', error);
            if (selectedChannel?.qrCodeBase64) {
                setDynamicQRCode(selectedChannel.qrCodeBase64);
            }
        }
    };

    const handleImagePick = async () => {
        try {
            const result = await pickImage();
            if (result) {
                setProofBase64(result.base64);
            }
        } catch (error) {
            alert.showError('Error', 'Failed to pick image');
        }
    };

    const handleNextStep = () => {
        const amountNum = parseInt(amount);
        if (!amount || isNaN(amountNum) || amountNum < MIN_RECHARGE) {
            alert.showError('Error', `Minimum recharge is ${formatCurrency(MIN_RECHARGE)}`);
            return;
        }

        if (!selectedChannel) {
            alert.showError('Error', 'Please select a payment channel');
            return;
        }

        setTransitioning(true);
        setTimeout(() => {
            setStep(2);
            setTimeLeft(300);
            setTransitioning(false);
        }, 1500);
    };




    const handleSubmit = async () => {
        if (!proofBase64) {
            alert.showError('Error', 'Step 1: Please upload payment screenshot');
            return;
        }

        if (!utrNumber || utrNumber.length !== 12) {
            alert.showError('Error', 'Step 2: Please enter a valid 12-digit UTR number');
            return;
        }

        setSubmitting(true);
        try {
            await createRechargeRequest(parseInt(amount), utrNumber, proofBase64);
            alert.showSuccess(
                'Success',
                'Recharge request submitted successfully.',
                () => navigate(-1)
            );
        } catch (error) {
            alert.showError('Error', error.message || 'Failed to submit request');
        } finally {
            setSubmitting(false);
        }
    };

    const handleCopyUPI = async () => {
        if (selectedChannel?.upiId) {
            const success = await copyToClipboard(selectedChannel.upiId);
            if (success) {
                alert.showSuccess('Copied', 'UPI ID copied to clipboard');
            }
        }
    };

    if (loading) {
        return <LoadingOverlay visible message="Loading payment channels..." />;
    }

    if (step === 1) {
        return (
            <div className="recharge-premium-screen">
                <LoadingOverlay visible={transitioning} message="Preparing payment gateway..." />
                <div className="recharge-header-nav">
                    <button className="back-btn-recharge" onClick={() => navigate(-1)}>
                        <span className="material-icons">arrow_back</span>
                    </button>
                    <h1 className="recharge-title-nav">ADD FUNDS</h1>
                </div>

                <div className="recharge-content-area">
                    {/* Available Balance Card */}
                    <div className="recharge-balance-card">
                        <span className="balance-label-top">AVAILABLE BALANCE</span>
                        <h2 className="balance-value-top">{formatCurrency(userData?.rechargeBalance || 0)}</h2>
                    </div>

                    <div className="recharge-form-container">
                        <div className="recharge-input-group">
                            <label className="recharge-field-label">SELECT AMOUNT</label>
                            <div className="amount-input-box-recharge">
                                <span className="currency-label-recharge">₹</span>
                                <input
                                    type="number"
                                    className="main-amount-input-recharge"
                                    placeholder="0.00"
                                    value={amount}
                                    onChange={(e) => setAmount(e.target.value)}
                                />
                            </div>
                        </div>

                        <div className="quick-amounts-grid">
                            {QUICK_AMOUNTS.map((amt) => (
                                <button
                                    key={amt}
                                    className={`quick-amt-btn ${amount === String(amt) ? 'active' : ''}`}
                                    onClick={() => setAmount(String(amt))}
                                >
                                    ₹{amt}
                                </button>
                            ))}
                        </div>

                        <div className="recharge-input-group">
                            <label className="recharge-field-label">SELECT PAYMENT CHANNEL</label>
                            <div className="payment-channels-list">
                                {channels.map((channel, index) => (
                                    <div
                                        key={channel.id}
                                        className={`channel-item-premium ${selectedChannel?.id === channel.id ? 'active' : ''}`}
                                        onClick={() => setSelectedChannel(channel)}
                                    >
                                        <div className="channel-icon-box">
                                            <span className="material-icons">bolt</span>
                                        </div>
                                        <div className="channel-details">
                                            <span className="channel-main-name">{channel.name || `NEXTRON ${index + 1}`}</span>
                                            <span className="channel-sub-type">{channel.type || 'FLASH GATEWAY'}</span>
                                        </div>
                                        <div className="channel-radio-indicator">
                                            {selectedChannel?.id === channel.id && <div className="radio-dot-inner"></div>}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>

                        <button className="confirm-proceed-btn" onClick={handleNextStep}>
                            CONFIRM & PROCEED
                        </button>

                        <p className="secure-payment-notice">
                            SECURE 256-BIT SSL ENCRYPTED TRANSACTION
                        </p>
                    </div>
                </div>
            </div>
        );
    }

    // Step 2: Payment Gateway (Light Theme as per reference)
    return (
        <div className="payment-gateway-screen">
            <LoadingOverlay visible={submitting} message="Submitting request..." />

            <div className="gateway-header-bar">
                <button className="gateway-back-btn" onClick={() => setStep(1)}>
                    <span className="material-icons">arrow_back</span>
                </button>
                <h1 className="gateway-title">SECURE PAYMENT GATEWAY</h1>
                <div className="gateway-timer-pill">{formatTime(timeLeft)}</div>
            </div>

            <div className="gateway-scroll-content compact">
                {/* Amount Display Card */}
                <div className="payable-amount-card-new compact">
                    <span className="payable-label-new">PAYABLE AMOUNT</span>
                    <h2 className="payable-value-new">₹{amount}.00</h2>
                </div>

                {/* QR Section with UPI ID below */}
                <div className="qr-dashed-container compact">
                    <div className="qr-recommended-badge">✓ RECOMMENDED — NO AMOUNT LIMIT</div>
                    <div className="qr-white-card">
                        {dynamicQRCode ? (
                            <img src={dynamicQRCode} alt="Payment QR" className="qr-image-actual" />
                        ) : (
                            <div className="qr-loading">GENERATING QR...</div>
                        )}
                    </div>
                    <div className="qr-footer-text">
                        <h3>SCAN QR CODE TO PAY</h3>
                        <p>Open any UPI app → Scan → Pay</p>
                    </div>

                    {/* UPI ID inside QR container */}
                    <div className="copy-upi-box compact">
                        <input
                            type="text"
                            readOnly
                            value={selectedChannel?.upiId || ''}
                            className="upi-id-input-gateway"
                        />
                        <button className="upi-id-copy-btn" onClick={handleCopyUPI}>COPY</button>
                    </div>
                </div>

                {/* Supported Apps */}
                <div className="supported-apps-strip compact">
                    <div className="apps-logo-row">
                        <div className="supported-app-logo"><img src="/assets/images/paytm.jpg" alt="Paytm" /></div>
                        <div className="supported-app-logo"><img src="/assets/images/phonepe.webp" alt="PhonePe" /></div>
                        <div className="supported-app-logo"><img src="/assets/images/gpay.jpg" alt="Google Pay" /></div>
                        <div className="supported-app-logo"><img src="/assets/images/otherupi.jpg" alt="UPI" /></div>
                    </div>
                </div>

                <div className="submission-gate-area">
                    {/* Step 1: Upload */}
                    <div className="input-step-group">
                        <p className="step-label-text">STEP 1: UPLOAD PAYMENT SCREENSHOT</p>
                        <div className="screenshot-dashed-box" onClick={handleImagePick}>
                            {proofBase64 ? (
                                <img src={proofBase64} alt="Proof" className="proof-full-preview" />
                            ) : (
                                <div className="camera-icon-circle">
                                    <span className="material-icons">photo_camera</span>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Step 2: UTR */}
                    <div className="input-step-group">
                        <p className="step-label-text">STEP 2: ENTER 12-DIGIT UTR NUMBER</p>
                        <input
                            type="text"
                            placeholder="Ex: 3125 4485 1190"
                            className="utr-input-gateway"
                            value={utrNumber}
                            onChange={(e) => setUtrNumber(e.target.value.replace(/\D/g, '').slice(0, 12))}
                        />
                    </div>

                    {/* Notice Box */}
                    <div className="gateway-notice-box">
                        <span className="material-icons warning-mini-icon">warning</span>
                        <p>
                            <strong>NOTICE:</strong> Correct UTR and clear Screenshot are mandatory.
                            Fake submissions lead to immediate account termination.
                        </p>
                    </div>

                    <button className="gateway-submit-btn" onClick={handleSubmit} disabled={submitting}>
                        {submitting ? 'PROCESSING...' : 'SUBMIT RECHARGE'}
                    </button>
                </div>

                {/* How To Pay Instructions */}
                <div className="how-to-pay-steps compact">
                    <p className="gateway-section-title">HOW TO PAY</p>
                    <div className="pay-step-row">
                        <div className="pay-step-circle">1</div>
                        <span>Open any UPI app (PhonePe, Paytm, GPay etc.)</span>
                    </div>
                    <div className="pay-step-row">
                        <div className="pay-step-circle">2</div>
                        <span>Scan the QR code or copy UPI ID above</span>
                    </div>
                    <div className="pay-step-row">
                        <div className="pay-step-circle">3</div>
                        <span>Complete payment & note the UTR number</span>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default RechargeScreen;
